import React from 'react'
import styles from '../../tabs/review/RequestForReviews/RequestForReviews.module.scss'
import Button from '../../UI/Button/Button'
import Input from '../../UI/Input/Input'
import { ReactComponent as NewReview } from '../../assets/icons/new-review.svg'
import EditIcon from '../../assets/icons/edit-no-underline.svg'
import ReviewsQuestion from './ReviewsQuestion'
import { getRequestMoreInfo } from '../../api/getApi'
import { useDispatch, useSelector } from 'react-redux'
import { clearRequestQuestions, setRequestDescription, setRequestQuestion } from '../../redux/actions/reviews.action'
import { notify } from '../../helpers/notify'
import { putRequestForReview } from '../../api/putApi'
import { requestsHelper } from '../../helpers/reviews.helper'
import useClickOutside from '../../hooks/useClickOutside'
import UseSmartTooltip from '../../hooks/useSmartTooltip'
import moment from 'moment'
import AnimateHeight from 'react-animate-height'

function ReviewsRequestsListItem({ data }) {
  const dateFormat = useSelector(({ user }) => user.settings.date_format)

  const [isOpen, setIsOpen] = React.useState(false)
  const [isRatingAdded, setIsRatingAdded] = React.useState(false)
  const [description, setDescription] = React.useState(data.description || '')
  const dispatch = useDispatch()
  const isOpenRef = useClickOutside(() => setIsOpen(false))

  React.useEffect(() => {
    if (data.description) {
      setDescription(data.description)
      setIsRatingAdded(true)
    }
  }, [data.description])

  React.useEffect(() => {
    if (data.category?.toLowerCase() === 'replied' && (!data.questions || data.questions.length === 0)) {
      const id = data.id
      getRequestMoreInfo(id)
        .then((data) => {
          setDescription(data.description || description)
          dispatch(setRequestQuestion(data, id))
        })
        .catch((error) => {
          if (error) notify.error('Error', 'Something went wrong')
        })
    }
  }, [data.category, data.id, data.questions, dispatch, description])

  const dataDateToDateFormat = (originalDate) => (originalDate ? moment(originalDate).format(dateFormat) : 'current')

  const handleOpen = () => {
    const id = data.id

    if (isOpen) return
    setIsOpen(true)

    if (data.questions && data.questions.length > 0) {
      setDescription(data.description || description)
      return
    }

    getRequestMoreInfo(id)
      .then((data) => {
        setDescription(data.description || description)
        dispatch(setRequestQuestion(data, id))
      })
      .catch((error) => {
        setIsOpen(false)
        if (error) notify.error('Error', 'Something went wrong')
      })
  }

  const handleClick = () => {
    const id = data.id
    setIsOpen(!isOpen)

    if (!isOpen) {
      if (data.questions && data.questions.length > 0) {
        setDescription(data.description || description)
      } else {
        getRequestMoreInfo(id)
          .then((data) => {
            setDescription(data.description || description)
            dispatch(setRequestQuestion(data, id))
          })
          .catch((error) => {
            setIsOpen(false)
            if (error) notify.error('Error', 'Something went wrong')
          })
      }
    } else {
      if (data.category?.toLowerCase() !== 'replied') {
        dispatch(clearRequestQuestions())
      }
    }
  }

  const handleRejected = () => {
    const id = data.id

    getRequestMoreInfo(id)
      .then((data) => {
        data.category = 'rejected'
        dispatch(setRequestQuestion(data, id))
        putRequestForReview(requestsHelper(data), id)
          .then(() => notify.info('Success', 'Review successfully was rejected'))
          .catch(() => notify.error('Error', 'Something went wrong'))
      })
      .catch((error) => {
        setIsOpen(false)
        if (error) notify.error('Error', 'Something went wrong')
      })
    setIsOpen(false)
  }

  const handleWithdraw = () => {
    const id = data.id

    getRequestMoreInfo(id)
      .then((data) => {
        data.category = 'rejected'
        dispatch(setRequestQuestion(data, id))
        putRequestForReview(requestsHelper(data), id)
          .then(() => notify.info('Success', 'Review successfully was withdrawn'))
          .catch(() => notify.error('Error', 'Something went wrong'))
      })
      .catch((error) => {
        if (error) notify.error('Error', 'Something went wrong')
      })
  }

  const handleSend = () => {
    const id = data.id
    data.description = description
    data.category = 'replied'
    dispatch(setRequestDescription(data, id))
    putRequestForReview(requestsHelper(data), id)
      .then((data) => {
        if (!data.ok) {
          data.errors.forEach((item) => {
            notify.error('Error', item.message)
          })
        } else {
          notify.info('Success', 'Your review was successfully added')
          setIsOpen(false)
        }
      })
      .catch(() => {
        notify.error('Error', 'Something went wrong')
      })
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleAnswer = (answer = 0, id) => {
    data.questions.map((item) => {
      if (item.id === id && answer) {
        item.answer = answer
        setIsRatingAdded(true)
      }
      return item
    })
  }

  const handleDescription = (e) => {
    const value = e.target.value
    setDescription(value)
  }

  const isReplied = data.category?.toLowerCase() === 'replied'
  const isNew = data.category?.toLowerCase() === 'new'

  return (
    <div
      ref={isOpenRef}
      className={`${styles.item} ${isReplied ? styles.itemReplied : ''} ${isOpen ? styles.itemOpen : ''}`}
    >
      <div className={styles.cardHeader}>
        <div className={styles.profilePhoto}>
          <img src={data.image} alt='user' />
        </div>
        <div className={styles.profileRankAndVessel}>
          {data.category.toLowerCase() === 'new' && <NewReview className={styles.new_star} />}
          <p className={styles.reviewPrompt}>
            {data.category.toLowerCase() === 'replied' ? 'Your review for' : 'Do you want to leave a review'}
          </p>
          <p className={styles.name}>
            {data.name?.length > 3
              ? `${data.name.toLowerCase()}${data.category.toLowerCase() === 'replied' ? '' : '?'}`
              : 'No name'}
          </p>
          <p className={styles.rank}>
            {data.rank}
            <span className={styles.reviewDate}>
              ({dataDateToDateFormat(data.start_date)} - {dataDateToDateFormat(data.end_date)})
            </span>
          </p>

          {!isReplied && <p className={styles.vesselName}>{data?.vessel_name.toLowerCase()}</p>}

          {isOpen && isNew && (
            <div className={styles.actionLinksOpen}>
              <button className={`${styles.actionLink} ${styles.actionLinkDisabled}`} type='button'>
                <img src={EditIcon} alt='pencil' />
                Edit
              </button>
              <button className={styles.actionLink} onClick={handleRejected} type='button'>
                Withdraw
                <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M6 0C2.686 0 0 2.686 0 6C0 9.314 2.686 12 6 12C9.314 12 12 9.314 12 6C12 2.686 9.314 0 6 0ZM9 6.6H3V5.4H9V6.6Z'
                    fill='#43927c'
                  />
                </svg>
              </button>
            </div>
          )}

          {isReplied && (
            <ReviewsRequestsListItemControls
              onClick={handleClick}
              onRejected={handleRejected}
              onWithdraw={handleWithdraw}
              status={data.category?.toLowerCase()}
              variant='replied'
            />
          )}
          {data.category?.toLowerCase() !== 'replied' && !isOpen && (
            <div className={styles.buttonsWrapper}>
              <ReviewsRequestsListItemControls
                onClick={handleOpen}
                onRejected={handleRejected}
                onWithdraw={handleWithdraw}
                status={data.category?.toLowerCase()}
              />
            </div>
          )}
        </div>
      </div>

      <AnimateHeight duration={300} height={isOpen ? 'auto' : 0}>
        {isOpen && (
          <div className={styles.reviewArea}>
            <ul className={styles.questionsWrapper}>
              {data.questions &&
                data.questions.map((item, index) => (
                  <ReviewsQuestion
                    key={index}
                    id={item.id}
                    title={item.question}
                    answer={item.answer}
                    handleClick={handleAnswer}
                    isEditable={isOpen}
                  />
                ))}
            </ul>
            {isOpen ? (
              <>
                <div className={styles.message}>
                  <Input
                    textarea
                    placeholder='Write your review...'
                    onChange={(e) => handleDescription(e)}
                    value={description}
                  />
                </div>
                <div className={styles.sendButtons}>
                  <Button theme='bordered' size='normal' onClick={handleClose}>
                    Close
                  </Button>
                  <Button disabled={!isRatingAdded} theme='default' size='normal' onClick={handleSend}>
                    Send
                  </Button>
                </div>
              </>
            ) : (
              data.description && <div className={styles.reviewText}>{data.description}</div>
            )}
          </div>
        )}
      </AnimateHeight>
    </div>
  )
}

export default ReviewsRequestsListItem

function ReviewsRequestsListItemControls({ status, onClick, onRejected, onWithdraw, variant }) {
  switch (status) {
    case 'rejected':
      return (
        <div className={styles.buttons}>
          <Button data-confirm='yes' type='button' size='normal' theme='default' onClick={onClick}>
            Yes
          </Button>
        </div>
      )
    case 'new':
      return (
        <div className={styles.buttons}>
          <Button data-confirm='yes' type='button' size='normal' theme='bordered' onClick={onRejected}>
            No
          </Button>
          <Button data-confirm='yes' type='button' size='normal' theme='default' onClick={onClick}>
            Yes
          </Button>
        </div>
      )
    case 'replied':
      return (
        <div className={variant === 'replied' ? styles.actionLinksReplied : styles.actionLinks}>
          <button className={variant === 'replied' ? styles.actionLinkReplied : styles.actionLink} onClick={onClick}>
            {variant === 'replied' ? (
              'Edit'
            ) : (
              <>
                <img src={EditIcon} alt='pencil' />
                Edit
              </>
            )}
          </button>
          <button className={variant === 'replied' ? styles.actionLinkReplied : styles.actionLink} onClick={onWithdraw}>
            {variant === 'replied' ? (
              'Withdraw'
            ) : (
              <>
                <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path
                    d='M7 0C3.134 0 0 3.134 0 7C0 10.866 3.134 14 7 14C10.866 14 14 10.866 14 7C14 3.134 10.866 0 7 0ZM10.5 7.7H3.5V6.3H10.5V7.7Z'
                    fill='#43927c'
                  />
                </svg>
                Withdraw
              </>
            )}
          </button>
        </div>
      )
    default:
      return null
  }
}
